import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Clock,
  Zap,
  Info,
  Loader2,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBrands } from '@/lib/BrandsProvider';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import useBrandAutopostSettings from '@/components/brand/hooks/useBrandAutopostSettings';
import { useTimezone } from '@/hooks/useTimezone';
import ConnectionsTab from '@/components/brand/tabs/ConnectionsTab';
import { ContentCalendarView } from '@/components/content/components/ContentCalendarView';
import {
  useContentData,
  Content
} from '@/components/content/useContentData';
import {
  useContentDragDrop
} from '@/components/content/hooks';
import { CalendarDateType } from '@/components/content/types/contentManagement';
import { loadContentPreferences, saveContentPreferences } from '@/components/content/utils/contentPreferences';
import { NotificationPreferences } from '@/components/notifications/NotificationPreferences';

const Automation = () => {
  const [activeTab, setActiveTab] = useState('status');
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const { selectedBrand } = useBrands();
  const { session } = useAuth();
  const { timezone, formatDate: formatDateInUserTimezone } = useTimezone();
  const navigate = useNavigate();

  // Calendar-related state
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const savedPreferences = loadContentPreferences();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [calendarDateType, setCalendarDateType] = useState<CalendarDateType>(savedPreferences.calendarDateType);

  const { fetchContentsByBrand } = useContentData();
  
  // Debug logging on component mount
  console.log('[Automation Debug] Component mounted with:', {
    selectedBrand,
    hasSelectedBrand: !!selectedBrand,
    session: !!session,
    timezone
  });

  // User email notifications state
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState<boolean>(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // Use the brand autopost settings hook with update function
  const {
    brandAutopostSettings,
    loading: autopostLoading,
    updateBrandAutopostSettings
  } = useBrandAutopostSettings(selectedBrand?.id);

  // Content drag and drop for calendar view
  const contentDragDrop = useContentDragDrop({
    onRefresh: loadContents,
    currentMonth
  });
  
  // Fetch user profile data for email notifications setting
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setProfileLoading(true);
        const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null };

        if (!account?.account_id) {
          console.error('No account found');
          return;
        }

        const { data, error } = await supabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .select('notificationPreferences')
          .eq('id', account.account_id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          return;
        }

        // Parse notificationPreferences if it's a JSON string
        const notificationPreferences = typeof data.notificationPreferences === 'string'
          ? JSON.parse(data.notificationPreferences)
          : data.notificationPreferences;

        setEmailNotificationsEnabled(notificationPreferences?.emailNotifications ?? true);
      } catch (error) {
        console.error('Error in fetchProfileData:', error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Load contents when brand changes (for calendar view)
  useEffect(() => {
    if (selectedBrand?.id) {
      loadContents();
    }
  }, [selectedBrand?.id]);

  // Save calendar date type preference when it changes
  useEffect(() => {
    saveContentPreferences({ calendarDateType });
  }, [calendarDateType]);

  // Scroll to top when active tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  // Debug logging for selectedBrand
  useEffect(() => {
    console.log('[Automation Debug] selectedBrand changed:', {
      selectedBrand,
      hasId: !!selectedBrand?.id,
      brandName: selectedBrand?.brand_name,
      status: selectedBrand?.status
    });
  }, [selectedBrand]);

  // Load contents for calendar view
  async function loadContents() {
    if (!selectedBrand?.id) return;

    try {
      setLoading(true);
      console.log('Loading contents for brand:', selectedBrand.id);

      const fetchedContents = await fetchContentsByBrand(selectedBrand.id);
      console.log('Fetched contents:', fetchedContents?.length);

      setContents(fetchedContents || []);
    } catch (error) {
      console.error('Error loading contents:', error);
    } finally {
      setLoading(false);
    }
  }

  // Event handlers for calendar view
  const handleViewEdit = (contentId: string) => {
    navigate(`/content/${contentId}`);
  };

  const handlePlatformIconClick = (e: React.MouseEvent, platformKey: string | null) => {
    e.stopPropagation();
    if (!selectedBrand?.id || !platformKey) return;

    // Navigate to platform connections page with the specific platform
    navigate(`/brands/${selectedBrand.id}/connections`, {
      state: { highlightPlatform: platformKey }
    });
  };

  // Workflow Dialog Component
  const WorkflowDialog = () => (
    <Dialog open={workflowDialogOpen} onOpenChange={setWorkflowDialogOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>How Automation Works</DialogTitle>
          <DialogDescription>
            Step-by-step process of how content is automatically created and posted
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          <div className="bg-blue-50/50 rounded-lg p-4 mb-6 border border-blue-100">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Hourly Autopost System
            </h4>
            <p className="text-sm text-blue-700 leading-relaxed">
              The autopost worker runs every hour to check which brands need content based on their frequency settings. This ensures timely, consistent content delivery that respects your scheduling preferences.
            </p>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">1</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Convergence Detection</h4>
              <p className="text-sm text-muted-foreground mb-2">
                News content is analyzed and convergences are identified based on your brand's focus areas.
              </p>
              <Badge variant="secondary">Continuous Monitoring</Badge>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">2</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Relevance Ranking</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Convergences are ranked by relevance to your brand. You'll receive notifications for highly relevant convergences.
              </p>
              <Badge variant="secondary">AI-Powered Analysis</Badge>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">3</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Campaign Creation</h4>
              <p className="text-sm text-muted-foreground mb-2">
                A new campaign is automatically created for the convergence with your configured prefix.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">Automatic Organization</Badge>
                <Button asChild size="sm" variant="outline">
                  <Link to="/campaigns">View Campaigns</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">4</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Content Generation</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Content is generated for each enabled platform based on your brand voice.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">Platform-Specific</Badge>
                <Button asChild size="sm" variant="outline">
                  <Link to="/content">View Content</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">5</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Smart Post Scheduling</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Content is intelligently scheduled based on your platform frequency settings. The system automatically finds the next available time slot if your preferred time is occupied, preventing conflicts.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">Frequency-Aware</Badge>
                <Badge variant="secondary">Conflict-Free</Badge>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold">6</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Notification</h4>
              <p className="text-sm text-muted-foreground mb-2">
                You receive an email notification when content is ready for review (if enabled).
              </p>
              <Badge variant="secondary">Optional Alerts</Badge>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto p-6 max-w-6xl">
        <WorkflowDialog />
        
      {/* Enhanced Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm mb-6 md:mb-8 p-4 md:p-8">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
        <div className="relative">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="p-2 md:p-3 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg">
                <Zap className="h-5 w-5 md:h-8 md:w-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 md:mb-2">
                  <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">Automation</h1>
                  {/* Tooltip icon - visible on all screen sizes */}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          type="button"
                          className="p-1.5 rounded-full hover:bg-blue-100/50 transition-colors flex-shrink-0"
                          aria-label="Automation information"
                        >
                          <Info className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs p-3">
                        <p className="text-sm leading-relaxed">
                          Understand how Rainfactory AI automates your content creation and posting through intelligent convergence detection, content generation, and smart scheduling.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {/* Desktop-only description text - mobile users see tooltip instead */}
                <p className="text-gray-600 text-base md:text-lg leading-relaxed hidden md:block">
                  Understand how Rainfactory AI automates your content creation and posting
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-1">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
          <TabsList className="relative grid w-full grid-cols-3 bg-transparent border-0 gap-1">
            <TabsTrigger value="status" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">Current Status</TabsTrigger>
            <TabsTrigger value="calendar" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">Calendar</TabsTrigger>
            <TabsTrigger value="notifications" className="relative bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-all duration-300 font-medium">Notifications</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="status" className="space-y-6">
          {/* Alert Banner: Auto-Generation Disabled */}
          {!profileLoading && brandAutopostSettings?.autogenerate_enabled === false && (
            <Alert variant="destructive" className="border-red-300 bg-red-50">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <AlertTitle className="text-red-900 font-semibold">Brand Level auto-content generation is not enabled</AlertTitle>
              <AlertDescription className="text-red-800">
                <p>The system will not generate content automatically. Enable auto generation using the Brand Automation Master Control below.</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Alert Banner: Email Notifications Disabled */}
          {!profileLoading && !emailNotificationsEnabled && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-900 font-semibold">Email Notifications not enabled!</AlertTitle>
              <AlertDescription className="text-amber-800">
                <p className="mb-3">You won't be receiving any emails about automated content unless you enable email notifications in your profile settings.</p>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="bg-white hover:bg-amber-50 border-amber-300 text-amber-700 hover:text-amber-900"
                >
                  <Link to="/profile">Edit Profile Settings</Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Brand-level automation master toggle */}
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-lg">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-lg md:text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                    Brand Automation Master Control
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Enable or disable all automation features for {selectedBrand?.brand_name || 'this brand'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={brandAutopostSettings?.autogenerate_enabled || false}
                    onCheckedChange={(checked) => updateBrandAutopostSettings({ autogenerate_enabled: checked })}
                    aria-label="Enable automation for brand"
                    className="data-[state=checked]:bg-green-500 data-[state=checked]:hover:bg-green-600"
                    disabled={autopostLoading}
                  />
                  <span className="font-medium text-gray-700">
                    {brandAutopostSettings?.autogenerate_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Connections Integration */}
          {selectedBrand?.id && (
            <div className="space-y-4">
              <ConnectionsTab brandId={selectedBrand.id} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-purple-50/20" />
              <div className="relative">
                <ContentCalendarView
                  contents={contents}
                  currentMonth={currentMonth}
                  onCurrentMonthChange={setCurrentMonth}
                  dateType={calendarDateType}
                  onDateTypeChange={setCalendarDateType}
                  dragOverDay={contentDragDrop.dragOverDay}
                  onDragStart={contentDragDrop.handleDragStart}
                  onDragEnd={contentDragDrop.handleDragEnd}
                  onDragOver={contentDragDrop.handleDragOver}
                  onDragLeave={contentDragDrop.handleDragLeave}
                  onDrop={contentDragDrop.handleDrop}
                  isContentDraggable={contentDragDrop.isContentDraggable}
                  onViewEdit={handleViewEdit}
                  onPlatformIconClick={handlePlatformIconClick}
                  formatDateInUserTimezone={formatDateInUserTimezone}
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Manage your email notification preferences for {selectedBrand?.brand_name || 'this brand'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedBrand?.id ? (
                <NotificationPreferences brandId={selectedBrand.id} />
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Please select a brand to manage notification preferences.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Automation;